<script lang="ts" setup>
    import '../../../css/choice.css';
    import { onMounted, reactive, computed, onUnmounted, nextTick } from 'vue';
    import { initPinia } from '../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore' 
    import { AppScreens, PrivilegeType } from "../../Enums/ChoiceEnums";
    import { IQuickPayIDResponse, IQuickPayInsuredItem } from '../../lib/Models/QuickPayModel';
    import { ModalStyles, popAlert } from "../../lib/Notification";
    import { openWindowFromVue } from '../../lib/Models/CommonItemsModel';   
    import { IToolbar, ToolbarStyle, ToolbarBackground, ToolbarPadding } from "../../lib/Toolbar";
    import { LayoutOrientation } from '../../Enums/UiEnums'; 
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import RadioButtonInput from '../../components/RadioButtonInput.vue'; 
    import Toolbar from "../../components/Toolbar.vue";
    import * as ChoiceApi from '../../lib/ChoiceApi';
    import * as OfficeMdImages from '../../lib/OfficeMd/images'

    const privilegesStore = usePrivilegesStore(initPinia());

    const labelClass = "labelColWidth pe-1 label-isalus-small isalus-label-secondary";
    const dataClass = "col-sm label-isalus-small";

    enum CardToggle {
        firstCard,
        secondCard
    }

    const props = defineProps<{
        patientId: number,
        patientEid: number,
        appointmentId?: number,
        scheduleDate?: string,
        tabId: number,
        patientName?: string
    }>();

    const photoConfig = reactive<IToolbar>({
        toolbarList: [
            {identifier: "", label: "Take Photo", tooltip:"Take a new photo of the patient.", action: ActionButtonStyle.Camera, performAction: () => onOpenHeadshot()},
            {identifier: "", label: "Scan", tooltip:"Scan a new photograph.", action: ActionButtonStyle.Import, performAction: () => onScanHeadshot()},
            {identifier: "", label: "Display Photos", tooltip:"Display the Photos.", action: ActionButtonStyle.Card, performAction: () => loadPatientPhotographs()},
            {identifier: "", label: "", tooltip:"Refresh.", action: ActionButtonStyle.Refresh, performAction: () => onRefresh()},
        ]
    });

    const patientIdConfig = reactive<IToolbar>({
        toolbarList: [
            {identifier: "", label: "Scan", tooltip:"Scan a new card.", action: ActionButtonStyle.Import, performAction: () => onScanPatientId()},
            {identifier: "", label: "Display Patient Ids", tooltip:"Display the Patient Ids.", action: ActionButtonStyle.Card, performAction: () => loadPatientIds()},
            {identifier: "", label: "", tooltip:"Refresh.", action: ActionButtonStyle.Refresh, performAction: () => onRefresh()},
        ]
    });

    const primaryConfig = reactive<IToolbar>({
        toolbarList: [
            {identifier: "", label: "Edit Insurance", tooltip:"Edit the Insurance Record.", action: ActionButtonStyle.Edit, performAction: () => onInsuranceEdit()},
            {identifier: "", label: "Scan", tooltip:"Scan a new card.", action: ActionButtonStyle.Import, performAction: () => onScanPrimary()},
            {identifier: "", label: "Display Cards", tooltip:"Display the Primary Insurance Cards.", action: ActionButtonStyle.Card, performAction: () => loadPrimaryInsuranceCards()},
            {identifier: "", label: "", tooltip:"Refresh.", action: ActionButtonStyle.Refresh, performAction: () => onRefresh()},
        ]
    });

    const secondaryConfig = reactive<IToolbar>({
        toolbarList: [
            {identifier: "", label: "Edit Insurance", tooltip:"Edit the Insurance Record.", action: ActionButtonStyle.Edit, performAction: () => onInsuranceEdit()},
            {identifier: "", label: "Scan", tooltip:"Scan a new card.", action: ActionButtonStyle.Import, performAction: () => onScanSecondary()},
            {identifier: "", label: "Display Cards", tooltip:"Display the Secondary Insurance Cards.", action: ActionButtonStyle.Card, performAction: () => loadSecondaryInsuranceCards()},
            {identifier: "", label: "", tooltip:"Refresh.", action: ActionButtonStyle.Refresh, performAction: () => onRefresh()},
        ]
    });

    const pharmacyBenefitConfig = reactive<IToolbar>({
        toolbarList: [
            {identifier: "", label: "Scan", tooltip:"Scan a new card.", action: ActionButtonStyle.Import, performAction: () => onScanPharmacyBenefit()},
            {identifier: "", label: "Display Pharmacy Benefit Cards", tooltip:"Display the Patient Pharmacy Benefit Cards.", action: ActionButtonStyle.Card, performAction: () => loadPharmacyBenefit()},
            {identifier: "", label: "", tooltip:"Refresh.", action: ActionButtonStyle.Refresh, performAction: () => onRefresh()},
        ]
    });

    const formData = reactive<{
        result?: IQuickPayIDResponse,
        primaryInsurance?: IQuickPayInsuredItem,
        secondaryInsurance?: IQuickPayInsuredItem,
        primaryCardToggle: CardToggle,
        secondaryCardToggle: CardToggle,
        pharmacyBenefitCardToggle: CardToggle   
    }>({
        primaryCardToggle: CardToggle.firstCard,
        secondaryCardToggle: CardToggle.firstCard,
        pharmacyBenefitCardToggle: CardToggle.firstCard,
    });

    async function onRefresh() {
        formData.result = undefined;
        await nextTick();
        formData.result = await ChoiceApi.quickPay.IDs(props.patientId);
        if (!formData.result)
            return;
        const primaryIndex = formData.result.insuredList.findIndex(o => o.coverage == 1);
        const secondaryIndex = formData.result.insuredList.findIndex(o => o.coverage == 2);
        formData.primaryInsurance = formData.result.insuredList[primaryIndex];
        formData.secondaryInsurance = formData.result.insuredList[secondaryIndex];
    }

    async function getPrivileges() {
        await privilegesStore.getScreens([AppScreens.CheckInOutDashboard, AppScreens.Documents, AppScreens.PatientSetup, AppScreens.HeadshotCapture]);
    }

    async function fetchData() {
        const promiseRefresh = onRefresh();
        const promisePrivileges = getPrivileges();
        await Promise.all([promiseRefresh, promisePrivileges]);
    }
    fetchData();

    onMounted(async () => {
        MessageLib.Edocument.sink.refreshHeadshot(refreshHeadshotSink);
    });

    onUnmounted(async () => {
        MessageLib.Edocument.sink.refreshHeadshotDeleteSink(refreshHeadshotSink);
    });

    const headshotUrl = computed<string>(() => formData.result?.ids.imageIdPhotograph ? `/officemd/api/EDocument/GetDocument/${formData.result?.ids.imageIdPhotograph}` : "/officemd/images/no_photo.png");
    const patientIdUrl = computed<string>(() => formData.result?.ids.imageIdPatientIdCard ? `/officemd/api/EDocument/GetDocument/${formData.result?.ids.imageIdPatientIdCard}` : "/officemd/images/no_photo.png");
    const primaryUrl1 = computed<string>(() => formData.result?.ids.imageIdPrimaryInsuranceCard1 ? `/officemd/api/EDocument/GetDocument/${formData.result?.ids.imageIdPrimaryInsuranceCard1}` : OfficeMdImages.blankImageUrl);
    const primaryUrl2 = computed<string>(() => formData.result?.ids.imageIdPrimaryInsuranceCard2 ? `/officemd/api/EDocument/GetDocument/${formData.result?.ids.imageIdPrimaryInsuranceCard2}` : OfficeMdImages.blankImageUrl);
    const secondaryUrl1 = computed<string>(() => formData.result?.ids.imageIdSecondaryInsuranceCard1 ? `/officemd/api/EDocument/GetDocument/${formData.result?.ids.imageIdSecondaryInsuranceCard1}` : OfficeMdImages.blankImageUrl);
    const secondaryUrl2 = computed<string>(() => formData.result?.ids.imageIdSecondaryInsuranceCard2 ? `/officemd/api/EDocument/GetDocument/${formData.result?.ids.imageIdSecondaryInsuranceCard2}` : OfficeMdImages.blankImageUrl);
    const pharmacyBenefitUrl1 = computed<string>(() => formData.result?.ids.imageIdPharmacyBenefitCard1 ? `/officemd/api/EDocument/GetDocument/${formData.result?.ids.imageIdPharmacyBenefitCard1}` : OfficeMdImages.blankImageUrl);
    const pharmacyBenefitUrl2 = computed<string>(() => formData.result?.ids.imageIdPharmacyBenefitCard2 ? `/officemd/api/EDocument/GetDocument/${formData.result?.ids.imageIdPharmacyBenefitCard2}` : OfficeMdImages.blankImageUrl);
    
    async function loadPatientPhotographs() {
        if (privilegesStore.screensWithReadPrivileges[AppScreens.Documents] && (formData.result?.ids.maxPrivilegePhotograph ?? 0) > PrivilegeType.Read) {
            const windowId = AppScreens.PrimaryInsuranceCard;
            const windowUrl = `/officemd/screens/ischedule_document.htm?Opener=NM&Type=PP&Title=${props.patientName}&SUID=0&PID=${props.patientId}&PEID=${props.patientEid}`;
            const windowSettings = { noTimestamp: "Y" };
            openWindowFromVue(windowId, windowUrl, windowSettings);            
        } else {
            popAlert(`You currently do not have access to eDocuments or the ${formData.result?.ids.menuPathPhotograph} folder.  Please contact your System Administrator.`, "No Access", ModalStyles.warning);
        }
    }

    async function loadPatientIds() {
        if (privilegesStore.screensWithReadPrivileges[AppScreens.Documents] && (formData.result?.ids.maxPrivilegePatientIdCard ?? 0) > PrivilegeType.Read) {
            const windowId = AppScreens.PrimaryInsuranceCard;
            const windowUrl = `/officemd/screens/ischedule_document.htm?Opener=NM&Type=PI&Title=${props.patientName}&SUID=0&PID=${props.patientId}&PEID=${props.patientEid}`;
            const windowSettings = { noTimestamp: "Y" };
            openWindowFromVue(windowId, windowUrl, windowSettings);            
        } else {
            popAlert(`You currently do not have access to eDocuments or the ${formData.result?.ids.menuPathPatientIdCard} folder.  Please contact your System Administrator.`, "No Access", ModalStyles.warning);
        }        
    }

    async function loadPrimaryInsuranceCards() {
        if (privilegesStore.screensWithReadPrivileges[AppScreens.Documents] && (formData.result?.ids.maxPrivilegePrimaryInsuranceCard ?? 0) > PrivilegeType.Read) {
            const windowId = AppScreens.PrimaryInsuranceCard;
            const windowUrl = `/officemd/screens/ischedule_document.htm?Opener=NM&Type=I1&Title=${props.patientName}&SUID=0&PID=${props.patientId}&PEID=${props.patientEid}`;
            const windowSettings = { noTimestamp: "Y" };
            openWindowFromVue(windowId, windowUrl, windowSettings);            
        } else {
            popAlert(`You currently do not have access to eDocuments or the ${formData.result?.ids.menuPathSecondaryInsuranceCard} folder.  Please contact your System Administrator.`, "No Access", ModalStyles.warning);
        }
    }

    async function loadSecondaryInsuranceCards() {
        if (privilegesStore.screensWithReadPrivileges[AppScreens.Documents] && (formData.result?.ids.maxPrivilegeSecondaryInsuranceCard ?? 0) > PrivilegeType.Read) {
            const windowId = AppScreens.SecondaryInsuranceCard;
            const windowUrl = `/officemd/screens/ischedule_document.htm?Opener=NM&Type=I2&Title=${props.patientName}&SUID=0&PID=${props.patientId}&PEID=${props.patientEid}`;
            const windowSettings = { noTimestamp: "Y" };
            openWindowFromVue(windowId, windowUrl, windowSettings);            
        } else {
            popAlert(`You currently do not have access to eDocuments or the ${formData.result?.ids.menuPathSecondaryInsuranceCard} folder.  Please contact your System Administrator.`, "No Access", ModalStyles.warning);
        }
    }

    async function loadPharmacyBenefit() {
        if (privilegesStore.screensWithReadPrivileges[AppScreens.Documents] && (formData.result?.ids.maxPrivilegePharmacyBenefitCard ?? 0) > PrivilegeType.Read) {
            const windowId = AppScreens.PrimaryInsuranceCard;
            const windowUrl = `/officemd/screens/ischedule_document.htm?Opener=NM&Type=PB&Title=${props.patientName}&SUID=0&PID=${props.patientId}&PEID=${props.patientEid}`;
            const windowSettings = { noTimestamp: "Y" };
            openWindowFromVue(windowId, windowUrl, windowSettings);            
        } else {
            popAlert(`You currently do not have access to eDocuments or the ${formData.result?.ids.maxPrivilegePharmacyBenefitCard} folder.  Please contact your System Administrator.`, "No Access", ModalStyles.warning);
        }        
    }

    async function onInsuranceEdit() {
        if (privilegesStore.screensWithReadPrivileges[AppScreens.PatientSetup]) {
            const windowId = AppScreens.PatientSetup;
            const windowUrl = `/officemd/screens/WindowSetup.htm?WT=Patient Setup&Qualifier=patient&WindowID=${props.patientId}&tab=Insurance`;
            const windowSettings = { noTimestamp: "Y" };
            openWindowFromVue(windowId, windowUrl, windowSettings);
        } else {
            popAlert(`You currently do not have access to the Patient Setup Screen.  Please contact your System Administrator.`, "No Access", ModalStyles.warning);
        }
    }

    async function onScanPatientId() {
        if (privilegesStore.screensWithWritePrivileges[AppScreens.Documents]) {
            const windowId = "DocumentScan";
            const windowUrl = `/officemd/document/documentscan/1/${props.patientEid}/${formData.result?.ids.menuIdPatientIdCard}/0`;
            const windowSettings = { height: 710, width: 1044, noTimestamp: "Y", resize: false, scrollbar: "no" };
            openWindowFromVue(windowId, windowUrl, windowSettings);
        } else {
            popAlert(`You currently do not have access to eDocuments to scan.  Please contact your System Administrator.`, "No Access", ModalStyles.warning);
        }
    }

    async function onScanPrimary() {
        if (privilegesStore.screensWithWritePrivileges[AppScreens.Documents]) {
            const windowId = "DocumentScan";
            const windowUrl = `/officemd/document/documentscan/1/${props.patientEid}/${formData.result?.ids.menuIdPrimaryInsuranceCard}/0`;
            const windowSettings = { height: 710, width: 1044, noTimestamp: "Y", resize: false, scrollbar: "no" };
            openWindowFromVue(windowId, windowUrl, windowSettings);
        } else {
            popAlert(`You currently do not have access to eDocuments to scan.  Please contact your System Administrator.`, "No Access", ModalStyles.warning);
        }
    }

    async function onScanSecondary() {
        if (privilegesStore.screensWithWritePrivileges[AppScreens.Documents]) {
            const windowId = "DocumentScan";
            const windowUrl = `/officemd/document/documentscan/1/${props.patientEid}/${formData.result?.ids.menuIdSecondaryInsuranceCard}/0`;
            const windowSettings = { height: 710, width: 1044, noTimestamp: "Y", resize: false, scrollbar: "no" };
            openWindowFromVue(windowId, windowUrl, windowSettings);
        } else {
            popAlert(`You currently do not have access to eDocuments to scan.  Please contact your System Administrator.`, "No Access", ModalStyles.warning);
        }
    }

    async function onScanPharmacyBenefit() {
        if (privilegesStore.screensWithWritePrivileges[AppScreens.Documents]) {
            const windowId = "DocumentScan";
            const windowUrl = `/officemd/document/documentscan/1/${props.patientEid}/${formData.result?.ids.menuIdPharmacyBenefitCard}/0`;
            const windowSettings = { height: 710, width: 1044, noTimestamp: "Y", resize: false, scrollbar: "no" };
            openWindowFromVue(windowId, windowUrl, windowSettings);
        } else {
            popAlert(`You currently do not have access to eDocuments to scan.  Please contact your System Administrator.`, "No Access", ModalStyles.warning);
        }
    }

    async function onOpenHeadshot() {
        if (privilegesStore.screensWithWritePrivileges[AppScreens.HeadshotCapture]) {
            const windowId = "DocumentScan";
            const windowUrl = `/officemd/app/patient/HeadshotCapture/${props.patientId}`;
            const windowSettings = { height: 400, width: 780, noTimestamp: "Y" };
            openWindowFromVue(windowId, windowUrl, windowSettings);
        } else {
            popAlert(`You currently do not have access to capture a headshot photo.  Please contact your System Administrator.`, "No Access", ModalStyles.warning);
        }
    }

    async function onScanHeadshot() {
        if (privilegesStore.screensWithWritePrivileges[AppScreens.Documents]) {
            const windowId = "DocumentScan";
            const windowUrl = `/officemd/document/documentscan/1/${props.patientEid}/${formData.result?.ids.menuIdPhotograph}/1`;
            const windowSettings = { height: 710, width: 1044, noTimestamp: "Y", resize: false, scrollbar: "no" };
            openWindowFromVue(windowId, windowUrl, windowSettings);
        } else {
            popAlert(`You currently do not have access to eDocuments to scan.  Please contact your System Administrator.`, "No Access", ModalStyles.warning);
        }
    }    

    function refreshHeadshotSink(payload: any) {
        if (payload.patientId == props.patientId) {
            formData.result!.ids.imageIdPhotograph = payload.documentId;
        }
    }
</script>

<style scoped>
    .fixedHeadshotWidth {
        width: 200px;
    }
    .fixedCardHeight {
        height: 200px;
    }
    .fullWidth {
        width: 100%;
    }
    .labelColWidth {
        width: 100px;
    }
    .titleWidth {
        width: 210px;
    }
</style>

<template>
    <div class="row mt-2">
        <div class="col fw-bold label-isalus-medium isalus-section-header">
            Patient Photograph
        </div>
    </div>
    <div class="row ps-3 mt-2">
        <div class="col-4">
            <template v-if="formData.result?.ids.extensionPhotograph?.toLowerCase() == 'pdf'">
                <object type="application/pdf" :data="headshotUrl" class="border border-secondary">Patient Photoraph</object>
            </template>
            <template v-else>
                <img :src="headshotUrl" class="fixedHeadshotWidth border border-secondary" alt="img" />
            </template>
        </div>
        <div class="col">
            <Toolbar 
                :configuration="photoConfig" 
                :toolbarStyle="ToolbarStyle.outline" 
                :toolbarPadding="ToolbarPadding.paddingOnlyLeftRight"
                :toolbarBackground="ToolbarBackground.white"/>  
        </div>
    </div>
    <div class="row ps-3 mt-2">
        <div class="col label-isalus-small fst-italic">
            {{ formData.result?.ids.menuPathPhotograph }}
        </div>
    </div>
    <hr>
    <div class="row mt-2">
        <div class="col fw-bold label-isalus-medium isalus-section-header">
            Patient ID Card
        </div>
    </div>
    <div class="row ps-3 mt-2">
        <div class="col-4">
            <template v-if="formData.result?.ids.imageIdPatientIdCard">
                <template v-if="formData.result?.ids.extensionPatientIdCard?.toLowerCase() == 'pdf'">
                    <object type="application/pdf" :data="patientIdUrl" class="border border-secondary">Patient ID Card</object>
                </template>
                <template v-else>
                    <img :src="patientIdUrl" class="fullWidth border border-secondary" alt="img" />
                </template>
            </template>
            <template v-else>
                <div class="border border-secondary fixedCardHeight pt-1 ps-3 pe-3 text-danger label-isalus-small">
                    *** NOTE: Card not found but may exist. Please check for Patient Ids Cards 
                        <span @click="loadPatientIds" class="isalus-link">
                            here
                        </span>.  Please verify the "Patient Id Card Naming Standard" in company settings.
                </div>
            </template>
        </div>
        <div class="col">
            <Toolbar 
                :configuration="patientIdConfig" 
                :toolbarStyle="ToolbarStyle.outline" 
                :toolbarPadding="ToolbarPadding.paddingOnlyLeftRight"
                :toolbarBackground="ToolbarBackground.white"/>  
        </div>
    </div>
    <div class="row ps-3 mt-2">
        <div class="col label-isalus-small fst-italic">
            {{ formData.result?.ids.menuPathPatientIdCard }}
        </div>
    </div>

    <hr>
    <div class="row mt-2">
        <div class="titleWidth fw-bold label-isalus-medium isalus-section-header">
            Primary Insurance Card
        </div>
        <div class="col label-isalus-small mt-1" :title="'Toggle between the last two primary insurance cards'">
            <RadioButtonInput 
                :value="CardToggle.firstCard" 
                v-model="formData.primaryCardToggle" 
                :layout="LayoutOrientation.Horz">  
                <div class="label-isalus-small">Front</div>
            </RadioButtonInput>
            <RadioButtonInput 
                :value="CardToggle.secondCard" 
                v-model="formData.primaryCardToggle" 
                :layout="LayoutOrientation.Horz">  
                <div class="label-isalus-small">Back</div>
            </RadioButtonInput>
        </div>
    </div>
    <div class="row ps-3 mt-2">
        <div class="col-4">
            <div v-show="formData.primaryCardToggle == CardToggle.firstCard">
                <template v-if="formData.result?.ids.imageIdPrimaryInsuranceCard1">
                    <template v-if="formData.result?.ids.extensionPrimaryInsuranceCard1?.toLowerCase() == 'pdf'">
                        <object type="application/pdf" :data="primaryUrl1" class="border border-secondary">Primary Insurance Card Front</object>
                    </template>
                    <template v-else>
                        <img :src="primaryUrl1" class="fullWidth border border-secondary" alt="img" />
                    </template>
                </template>
                <template v-else>
                    <div class="border border-secondary fixedCardHeight pt-1 ps-3 pe-3 text-danger label-isalus-small">
                        *** NOTE: Card not found but may exist. Please check for Primary Insurance Cards 
                            <span @click="loadPrimaryInsuranceCards" class="isalus-link">
                                here
                            </span>.  Please verify the "Primary Insurance Card Naming Standard" in company settings.
                    </div>
                </template>
            </div>
            <div v-show="formData.primaryCardToggle == CardToggle.secondCard">
                <template v-if="formData.result?.ids.imageIdPrimaryInsuranceCard2">
                    <template v-if="formData.result?.ids.extensionPrimaryInsuranceCard2?.toLowerCase() == 'pdf'">
                        <object type="application/pdf" :data="primaryUrl2" class="border border-secondary">Primary Insurance Card Back</object>
                    </template>
                    <template v-else>
                        <img :src="primaryUrl2" class="fullWidth border border-secondary" alt="img" />
                    </template>
                </template>
                <template v-else>
                    <div class="border border-secondary fixedCardHeight pt-1 ps-3 pe-3 text-danger label-isalus-small">
                        *** NOTE: Card not found but may exist. Please check for Primary Insurance Cards 
                            <span @click="loadPrimaryInsuranceCards" class="isalus-link">
                                here
                            </span>.  Please verify the "Primary Insurance Card Naming Standard" in company settings.
                    </div>
                </template>
            </div>
        </div>
        <div class="col">
            <div class="row">
                <div class="col">
                    <Toolbar 
                        :configuration="primaryConfig" 
                        :toolbarStyle="ToolbarStyle.outline" 
                        :toolbarPadding="ToolbarPadding.paddingOnlyLeftRight"
                        :toolbarBackground="ToolbarBackground.white"/>  
                </div>
            </div>
            <div class="row mt-2">
                <div :class="labelClass">
                    Payer
                </div>
                <div :class="dataClass" v-if="formData.primaryInsurance">
                    {{ formData.primaryInsurance.payerName }}
                </div>
                <div class="w-100"></div>
                <div :class="labelClass">
                    Type
                </div>
                <div :class="dataClass" v-if="formData.primaryInsurance">
                    {{ formData.primaryInsurance.insuranceType }}
                </div>
                <div class="w-100"></div>
                <div :class="labelClass">
                    Member ID #
                </div>
                <div :class="dataClass" v-if="formData.primaryInsurance">
                    {{ formData.primaryInsurance.idCode }}
                </div>
                <div class="w-100"></div>
                <div :class="labelClass">
                    Group Policy #
                </div>
                <div :class="dataClass" v-if="formData.primaryInsurance">
                    {{ formData.primaryInsurance.groupPolicyNumber }}
                </div>
                <div class="w-100"></div>
                <div :class="labelClass">
                    Copay
                </div>
                <div :class="dataClass" v-if="formData.primaryInsurance">
                    {{ formData.primaryInsurance.copay }}
                </div>
                <div class="w-100"></div>
                <div :class="labelClass">
                    Start Date
                </div>
                <div :class="dataClass" v-if="formData.primaryInsurance">
                    {{ formData.primaryInsurance.startDate }}
                </div>
                <div class="w-100"></div>
                <div :class="labelClass">
                    Sig on File
                </div>
                <div :class="dataClass" v-if="formData.primaryInsurance">
                    {{ formData.primaryInsurance.signatureOnFileDate }}
                </div>
            </div>
        </div>
    </div>
    <div class="row ps-3 mt-2">
        <div class="col label-isalus-small fst-italic">
            {{ formData.result?.ids.menuPathPrimaryInsuranceCard }}
        </div>
    </div>

    <hr>
    <div class="row mt-2">
        <div class="titleWidth fw-bold label-isalus-medium isalus-section-header">
            Secondary Insurance Card
        </div>
        <div class="col label-isalus-small mt-1" :title="'Toggle between the last two secondary insurance cards'">
            <RadioButtonInput 
                :value="CardToggle.firstCard" 
                v-model="formData.secondaryCardToggle" 
                :layout="LayoutOrientation.Horz">  
                <div class="label-isalus-small">Front</div>
            </RadioButtonInput>
            <RadioButtonInput 
            :value="CardToggle.secondCard" 
                v-model="formData.secondaryCardToggle" 
                :layout="LayoutOrientation.Horz">  
                <div class="label-isalus-small">Back</div>
            </RadioButtonInput>
        </div>
    </div>
    <div class="row ps-3 mt-2">
        <div class="col-4">
            <div v-show="formData.secondaryCardToggle == CardToggle.firstCard">
                <template v-if="formData.result?.ids.imageIdSecondaryInsuranceCard1">
                    <template v-if="formData.result?.ids.extensionSecondaryInsuranceCard1?.toLowerCase() == 'pdf'">
                        <object type="application/pdf" :data="secondaryUrl1" class="border border-secondary">Secondary Insurance Card Front</object>
                    </template>
                    <template v-else>
                        <img :src="secondaryUrl1" class="fullWidth border border-secondary" alt="img" />
                    </template>
                </template>
                <template v-else>
                    <div class="border border-secondary fullWidth fixedCardHeight pt-1 ps-3 pe-3 text-danger label-isalus-small">
                        *** NOTE: Card not found but may exist. Please check for Secondary Insurance Cards 
                            <span @click="loadSecondaryInsuranceCards" class="isalus-link">
                                here
                            </span>.  Please verify the "Secondary Insurance Card Naming Standard" in company settings.
                    </div>
                </template>
            </div>
            <div v-show="formData.secondaryCardToggle == CardToggle.secondCard">
                <template v-if="formData.result?.ids.imageIdSecondaryInsuranceCard2">
                    <template v-if="formData.result?.ids.extensionSecondaryInsuranceCard2?.toLowerCase() == 'pdf'">
                        <object type="application/pdf" :data="secondaryUrl2" class="border border-secondary">Secondary Insurance Card Back</object>
                    </template>
                    <template v-else>
                        <img :src="secondaryUrl2" class="fullWidth border border-secondary" alt="img" />
                    </template>
                </template>
                <template v-else>
                    <div class="border border-secondary fullWidth fixedCardHeight pt-1 ps-3 pe-3 text-danger label-isalus-small">
                        *** NOTE: Card not found but may exist. Please check for Secondary Insurance Cards 
                            <span @click="loadSecondaryInsuranceCards" class="isalus-link">
                                here
                            </span>.  Please verify the "Secondary Insurance Card Naming Standard" in company settings.
                    </div>
                </template>
            </div>
        </div>
        <div class="col">
            <div class="row">
                <div class="col">
                    <Toolbar 
                        :configuration="secondaryConfig" 
                        :toolbarStyle="ToolbarStyle.outline" 
                        :toolbarPadding="ToolbarPadding.paddingOnlyLeftRight"
                        :toolbarBackground="ToolbarBackground.white"/>  
                </div>
            </div>
            <div class="row mt-2">
                <div :class="labelClass">
                    Payer
                </div>
                <div :class="dataClass" v-if="formData.secondaryInsurance">
                    {{ formData.secondaryInsurance.payerName }}
                </div>
                <div class="w-100"></div>
                <div :class="labelClass">
                    Type
                </div>
                <div :class="dataClass" v-if="formData.secondaryInsurance">
                    {{ formData.secondaryInsurance.insuranceType }}
                </div>
                <div class="w-100"></div>
                <div :class="labelClass">
                    Member ID #
                </div>
                <div :class="dataClass" v-if="formData.secondaryInsurance">
                    {{ formData.secondaryInsurance.idCode }}
                </div>
                <div class="w-100"></div>
                <div :class="labelClass">
                    Group Policy #
                </div>
                <div :class="dataClass" v-if="formData.secondaryInsurance">
                    {{ formData.secondaryInsurance.groupPolicyNumber }}
                </div>
                <div class="w-100"></div>
                <div :class="labelClass">
                    Copay
                </div>
                <div :class="dataClass" v-if="formData.secondaryInsurance">
                    {{ formData.secondaryInsurance.copay }}
                </div>
                <div class="w-100"></div>
                <div :class="labelClass">
                    Start Date
                </div>
                <div :class="dataClass" v-if="formData.secondaryInsurance">
                    {{ formData.secondaryInsurance.startDate }}
                </div>
                <div class="w-100"></div>
                <div :class="labelClass">
                    Sig on File
                </div>
                <div :class="dataClass" v-if="formData.secondaryInsurance">
                    {{ formData.secondaryInsurance.signatureOnFileDate }}
                </div>
            </div>
        </div>
    </div>
    <div class="row ps-3 mt-2">
        <div class="col label-isalus-small fst-italic">
            {{ formData.result?.ids.menuPathSecondaryInsuranceCard }}
        </div>
    </div>

    <hr>
    <div class="row mt-2">
        <div class="titleWidth fw-bold label-isalus-medium isalus-section-header">
            Pharmacy Benefit Card
        </div>
        <div class="col label-isalus-small mt-1" :title="'Toggle between the last two secondary insurance cards'">
            <RadioButtonInput 
                :value="CardToggle.firstCard" 
                v-model="formData.pharmacyBenefitCardToggle" 
                :layout="LayoutOrientation.Horz">  
                <div class="label-isalus-small">Front</div>
            </RadioButtonInput>
            <RadioButtonInput 
            :value="CardToggle.secondCard" 
                v-model="formData.pharmacyBenefitCardToggle" 
                :layout="LayoutOrientation.Horz">  
                <div class="label-isalus-small">Back</div>
            </RadioButtonInput>
        </div>
    </div>
    <div class="row ps-3 mt-2">
        <div class="col-4">
            <div v-show="formData.pharmacyBenefitCardToggle == CardToggle.firstCard">
                <template v-if="formData.result?.ids.imageIdPharmacyBenefitCard1">
                    <template v-if="formData.result?.ids.extensionPharmacyBenefitCard1?.toLowerCase() == 'pdf'">
                        <object type="application/pdf" :data="pharmacyBenefitUrl1" class="border border-secondary">Pharmacy Benefit Card</object>
                    </template>
                    <template v-else>
                        <img :src="pharmacyBenefitUrl1" class="fullWidth border border-secondary" alt="img" />
                    </template>
                </template>
                <template v-else>
                    <div class="border border-secondary fixedCardHeight pt-1 ps-3 pe-3 text-danger label-isalus-small">
                        *** NOTE: Card not found but may exist. Please check for Pharmacy Benefit Cards 
                            <span @click="loadPharmacyBenefit" class="isalus-link">
                                here
                            </span>.  Please verify the "Pharmacy Benefit Card Naming Standard" in company settings.
                    </div>
                </template>
            </div>
            <div v-show="formData.pharmacyBenefitCardToggle == CardToggle.secondCard">
                <template v-if="formData.result?.ids.imageIdPharmacyBenefitCard2">
                    <template v-if="formData.result?.ids.extensionPharmacyBenefitCard2?.toLowerCase() == 'pdf'">
                        <object type="application/pdf" :data="pharmacyBenefitUrl2" class="border border-secondary">Pharmacy Benefit Card</object>
                    </template>
                    <template v-else>
                        <img :src="pharmacyBenefitUrl2" class="fullWidth border border-secondary" alt="img" />
                    </template>
                </template>
                <template v-else>
                    <div class="border border-secondary fixedCardHeight pt-1 ps-3 pe-3 text-danger label-isalus-small">
                        *** NOTE: Card not found but may exist. Please check for Pharmacy Benefit Cards 
                            <span @click="loadPharmacyBenefit" class="isalus-link">
                                here
                            </span>.  Please verify the "Pharmacy Benefit Card Naming Standard" in company settings.
                    </div>
                </template>
            </div>
        </div>
        <div class="col">
            <Toolbar 
                :configuration="pharmacyBenefitConfig" 
                :toolbarStyle="ToolbarStyle.outline" 
                :toolbarPadding="ToolbarPadding.paddingOnlyLeftRight"
                :toolbarBackground="ToolbarBackground.white"/>  
        </div>
    </div>
    <div class="row ps-3 mt-2">
        <div class="col label-isalus-small fst-italic">
            {{ formData.result?.ids.menuPathPharmacyBenefitCard }}
        </div>
    </div>


</template>
